import React from 'react';
import { StyleSheet, View } from 'react-native';

import { Text } from '~/components/core';
import { StrategyExecution } from '~/api-services/AIStrategyBuilderService/types';
import { executionColor, executionMessage } from '~/constants/strategyConstants';
import { FONT_SIZE_XS } from '~/constants/typography';
import { scaleSize } from '~/constants/scale';
import { useStyles } from '~/hooks';
import { useTheme } from '~/hooks/useTheme';
import { Theme } from '~/theme';

import { formatStrategyDateTime } from '../../../utils/format';

type Props = {
    execution: StrategyExecution;
};

export default function ExecutionRow({ execution }: Props) {
    const styles = useStyles(stylesGenerator);
    const theme = useTheme();

    return (
        <View style={styles.row}>
            <View style={styles.header}>
                <View style={styles.outcome}>
                    {/* The wire code ("OPEN_POSITION_FAILED") is not something to show a client, so
                        the outcome rides on the dot plus the decoded message. */}
                    <View style={[styles.dot, { backgroundColor: executionColor(theme, execution.code) }]} />
                    <Text noTranslate style={styles.message}>
                        {executionMessage(execution.code, execution.message, execution.context)}
                    </Text>
                </View>
                <Text noTranslate style={styles.time}>
                    {formatStrategyDateTime(execution.created)}
                </Text>
            </View>
            {execution.positionHumanId ? (
                <Text noTranslate style={styles.position}>
                    {execution.positionHumanId}
                </Text>
            ) : null}
        </View>
    );
}

const stylesGenerator = (theme: Theme) =>
    StyleSheet.create({
        row: {
            paddingVertical: scaleSize(10),
            borderBottomWidth: StyleSheet.hairlineWidth,
            borderBottomColor: theme.tabIconDefault,
        },
        header: {
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
        },
        outcome: {
            flexDirection: 'row',
            alignItems: 'center',
            flexShrink: 1,
        },
        dot: {
            width: scaleSize(8),
            height: scaleSize(8),
            borderRadius: scaleSize(4),
            marginRight: scaleSize(8),
        },
        message: {
            color: theme.text,
            fontSize: FONT_SIZE_XS,
            flexShrink: 1,
        },
        time: {
            color: theme.textSecondary,
            fontSize: FONT_SIZE_XS,
            marginLeft: scaleSize(8),
        },
        position: {
            color: theme.textSecondary,
            fontSize: FONT_SIZE_XS,
            marginTop: scaleSize(4),
            marginLeft: scaleSize(16),
        },
    });
